import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Maximize2, Minimize2, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export function PdfPreview({ fileUrl }: { fileUrl: string }): JSX.Element {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pageWidth = useMemo(() => Math.round(720 * scale), [scale]);

  useEffect(() => {
    function onFullscreenChange(): void {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    }

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  function zoomIn(): void {
    setScale((current) => Math.min(2, Math.round((current + 0.1) * 10) / 10));
  }

  function zoomOut(): void {
    setScale((current) => Math.max(0.5, Math.round((current - 0.1) * 10) / 10));
  }

  function previousPage(): void {
    setPageNumber((current) => Math.max(1, current - 1));
  }

  function nextPage(): void {
    setPageNumber((current) => Math.min(numPages || 1, current + 1));
  }

  async function toggleFullscreen(): Promise<void> {
    const container = containerRef.current;
    if (!container) return;

    if (document.fullscreenElement === container) {
      await document.exitFullscreen();
      return;
    }

    await container.requestFullscreen();
  }

  return (
    <div
      ref={containerRef}
      className={isFullscreen ? 'h-screen overflow-auto bg-background p-4' : 'space-y-3'}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={previousPage} disabled={pageNumber <= 1}>
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          <div className="text-sm text-muted-foreground">
            Page <span className="font-medium text-foreground">{pageNumber}</span> of{' '}
            <span className="font-medium text-foreground">{numPages || 1}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={nextPage}
            disabled={pageNumber >= (numPages || 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={zoomOut} disabled={scale <= 0.5}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <div className="min-w-14 text-center text-sm text-muted-foreground">
            {Math.round(scale * 100)}%
          </div>
          <Button type="button" variant="outline" size="sm" onClick={zoomIn} disabled={scale >= 2}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => void toggleFullscreen()}>
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button type="button" variant="outline" size="sm" asChild>
            <a href={fileUrl} target="_blank" rel="noreferrer" aria-label="Open document in new tab">
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
      <div
        className={
          isFullscreen
            ? 'overflow-auto rounded-md border bg-background p-3'
            : 'h-[480px] overflow-auto rounded-md border bg-background p-3'
        }
      >
      <Document
        file={fileUrl}
        onLoadSuccess={({ numPages: pages }) => {
          setNumPages(pages);
          setPageNumber((current) => Math.min(current, pages || 1));
        }}
        loading={<div className="text-sm text-muted-foreground">Loading PDF…</div>}
        error={<div className="text-sm text-destructive">Failed to load the PDF preview.</div>}
      >
        <div className="flex justify-center">
          <div className="overflow-hidden rounded border bg-white shadow-sm">
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </div>
        </div>
      </Document>
    </div>
    </div>
  );
}
